// src/components/FormRenderer.js
import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./FormRenderer.css";
import ImageField from "./ImageField";
import LinkField from "./LinkField";
import MultiSelectField from "../components/Multiselect";

function SocialField({ handles = [], value, onChange, disabled }) {
  const social = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: 16,
        background: disabled ? "#f7f7f7" : "#fff",
      }}
    >
      {sortFields(handles).map((handle) => {
        const handleName = String(handle?.name || "").trim();
        if (!handleName) return null;
        return (
          <div key={handleName} style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
              {handle.label || handleName}
            </label>
            <input
              type="url"
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid #ccc",
                background: disabled ? "#f7f7f7" : "#fff",
              }}
              placeholder={handle.placeholder || "https://"}
              value={social[handleName] || ""}
              disabled={disabled}
              onChange={(event) => onChange({
                ...social,
                [handleName]: event.target.value,
              })}
            />
          </div>
        );
      })}
    </div>
  );
}

function CurrencyField({ value, onChange, disabled, required, placeholder }) {
  const [displayValue, setDisplayValue] = React.useState(() =>
    value === "" || value === null || value === undefined
      ? ""
      : Number(value).toFixed(2)
  );

  React.useEffect(() => {
    if (document.activeElement?.dataset?.currencyInput === "true") return;
    setDisplayValue(
      value === "" || value === null || value === undefined
        ? ""
        : Number(value).toFixed(2)
    );
  }, [value]);

  const commit = (raw) => {
    if (raw.trim() === "") {
      setDisplayValue("");
      onChange("");
      return;
    }
    const amount = Number(raw);
    if (!Number.isFinite(amount)) return;
    const formatted = amount.toFixed(2);
    setDisplayValue(formatted);
    onChange(Number(formatted));
  };

  return (
    <input
      data-currency-input="true"
      style={{
        width: "100%",
        padding: 10,
        borderRadius: 8,
        border: "1px solid #ccc",
        background: disabled ? "#f7f7f7" : "#fff",
      }}
      type="number"
      inputMode="decimal"
      step="0.01"
      placeholder={placeholder || "0.00"}
      value={displayValue}
      disabled={disabled}
      required={required}
      onChange={(event) => setDisplayValue(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
    />
  );
}


const QUILL_MODULES_BASIC = {
  toolbar: [
    ["bold", "italic", "underline"],
    //[{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    //["clean"],
  ],
};

function normalizeForm(input) {
  if (!input) return null;
  return input.form ? input.form : input; // allow {form:{...}} or {...}
}

function sortFields(fields = []) {
  return fields
    .map((field, index) => {
      const hasOrder = field?.order !== null &&
        field?.order !== undefined &&
        field?.order !== "" &&
        Number.isInteger(Number(field.order));

      return {
        field,
        index,
        order: hasOrder ? Number(field.order) : index + 1,
      };
    })
    .sort((a, b) => {
      return a.order - b.order || a.index - b.index;
    })
    .map(({ field }) => field);
}

export default function FormRenderer({
  form,
  values = {},
  onChange,
  disabled = false,
}) {
  const f = normalizeForm(form);
  if (!f) return null;

  const fields = sortFields(f.fields || []);

  function setValue(name, value) {
    const next = { ...values, [name]: value };
    onChange?.(next);
  }

  return (
    <div>
      {fields.map((field) => {
        const {
          label,
          name,
          type = "text",
          placeholder,
          options = [],
          required = false,
          description,
          hint,
        } = field || {};

        if (!name) return null;

        const value = values[name] ?? "";
        const isRequired = Boolean(required);
        const helpText = description || hint;

        const labelText = (
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            {label || name}
            {isRequired ? (
              <span style={{ color: "#c00", marginLeft: 4 }}>*</span>
            ) : null}
          </label>
        );

        const fieldDescription = helpText ? (
          <div style={{ marginTop: 4, color: "#555", fontSize: 13 }}>
            {helpText}
          </div>
        ) : null;

        const wrapStyle = { marginBottom: 14 };

        const renderLabelAndHelp = (
          <>
            {labelText}
            {fieldDescription}
          </>
        );

        // --- RENDER BY TYPE ---
        if (type === "textarea") {
          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <textarea
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  minHeight: 120,
                  background: disabled ? "#f7f7f7" : "#fff",
                }}
                placeholder={placeholder || ""}
                value={value}
                disabled={disabled}
                required={isRequired}
                onChange={(e) => setValue(name, e.target.value)}
              />
            </div>
          );
        }

        if (type === "select") {
          const current = String(values[name] ?? "").trim();

          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <select
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  background: disabled ? "#f7f7f7" : "#fff",
                }}
                value={current}
                disabled={disabled}
                required={isRequired}
                onChange={(e) => setValue(name, e.target.value)}
              >
                <option value="">Select…</option>

                {options.map((opt) => {
                  const rawValue =
                    typeof opt === "string" ? opt : opt.name ?? opt.value ?? opt.label;

                  const rawLabel =
                    typeof opt === "string" ? opt : opt.label ?? opt.name ?? opt.value;

                  const ov = String(rawValue ?? "").trim();
                  const ol = String(rawLabel ?? "").trim();

                  return (
                    <option key={ov} value={ov}>
                      {ol}
                    </option>
                  );
                })}
              </select>
            </div>
          );
        }

        if (type === "radio") {
          const currentValue = String(values[name] ?? "").trim();

          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {options.map((opt) => {
                  const rawValue =
                    typeof opt === "string" ? opt : opt.name ?? opt.value ?? opt.label;
                  const rawLabel =
                    typeof opt === "string" ? opt : opt.label ?? opt.name ?? opt.value;
                  const ov = String(rawValue ?? "").trim();
                  const ol = String(rawLabel ?? "").trim();
                  const checked = currentValue === ov;

                  return (
                    <label
                      key={ov}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="radio"
                        name={name}
                        value={ov}
                        checked={checked}
                        disabled={disabled}
                        required={isRequired}
                        onChange={(e) => setValue(name, e.target.value)}
                      />
                      <span>{ol}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        }

        if (type === "checkbox" || type === "boolean") {
          const checked = values[name] === true;
          return (
            <div key={name} style={wrapStyle}>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={name}
                  checked={checked}
                  disabled={disabled}
                  required={isRequired}
                  onChange={(e) => setValue(name, e.target.checked)}
                />
                <label className="form-check-label fw-semibold" htmlFor={name}>
                  {label || name}
                </label>
              </div>
              {fieldDescription}
            </div>
          );
        }

        if (type === "rtf" || type === "richtext") {
          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <div
                className="rich-text-editor"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  overflow: "visible",
                  background: disabled ? "#f7f7f7" : "#fff",
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={value}
                  onChange={(html) => setValue(name, html)}
                  modules={QUILL_MODULES_BASIC}
                  readOnly={disabled}
                />
              </div>
            </div>
          );
        }

        if (type === "image") {
          return (
            <ImageField
              key={name}
              label={label || name}
              name={name}
              value={values[name] || ""}
              disabled={disabled}
              onChange={(v) => setValue(name, v)}
              aspect={name === "logo" ? 1 : (field.aspect ?? 16 / 9)}
              mimeType={name === "logo" ? "image/png" : (field.mimeType || "image/jpeg")}
              helpText={helpText}
            />
          );
        }

        // ✅ Link field (supports both new "link" type and old "slug" type)
        if (type === "link" || type === "slug") {
          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <LinkField
                link={value}
                canEdit={!disabled}
                basePath={field.basePath || ""} // e.g. "posts"
                onChange={(nextLink) => setValue(name, nextLink)}
              />
            </div>
          );
        }

        if (type === "multiselect" || type === "tags") {
          const current = Array.isArray(values[name])
            ? values[name]
            : values[name]
            ? [values[name]]
            : [];

          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <MultiSelectField
                value={current}
                options={options}
                disabled={disabled}
                placeholder={placeholder || "Type to search…"}
                onChange={(arr) => setValue(name, arr)}
              />
            </div>
          );
        }

        if (type === "social") {
          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <SocialField
                handles={Array.isArray(field.handles) ? field.handles : []}
                value={values[name]}
                disabled={disabled}
                onChange={(nextValue) => setValue(name, nextValue)}
              />
            </div>
          );
        }

        if (type === "currency") {
          return (
            <div key={name} style={wrapStyle}>
              {renderLabelAndHelp}
              <CurrencyField
                value={value}
                disabled={disabled}
                required={isRequired}
                placeholder={placeholder}
                onChange={(nextValue) => setValue(name, nextValue)}
              />
            </div>
          );
        }

        // number/date/email/url/password/tel/text/default
        const inputType =
          type === "number"
            ? "number"
            : type === "date"
            ? "date"
            : type === "email"
            ? "email"
            : type === "url"
            ? "url"
            : type === "password"
            ? "password"
            : type === "phone" || type === "tel"
            ? "tel"
            : "text";

        return (
          <div key={name} style={wrapStyle}>
            {renderLabelAndHelp}
            <input
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid #ccc",
                background: disabled ? "#f7f7f7" : "#fff",
              }}
              type={inputType}
              placeholder={placeholder || ""}
              value={value}
              disabled={disabled}
              required={isRequired}
              onChange={(e) => {
                const v =
                  inputType === "number"
                    ? e.target.value === ""
                      ? ""
                      : Number(e.target.value)
                    : e.target.value;
                setValue(name, v);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
